import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, ProgressBar, Badge, ListGroup, Spinner } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import { db, auth } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import LearnerNavbar from '../components/LearnerNavbar';
import FacilitatorNavbar from '../components/FacilitatorNavbar';
import AdminNavbar from '../components/AdminNavbar';

const ModulePage = () => {
  const { moduleId } = useParams();
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const role = userProfile?.role;
  const [module, setModule] = useState(null);
  const [userProgress, setUserProgress] = useState(null);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [selectedLessonId, setSelectedLessonId] = useState(null);
  const [loading, setLoading] = useState(true);

  const backRoute = role === 'admin' ? '/admin' : role === 'facilitator' ? '/facilitator' : '/dashboard';
  const Navbar = role === 'admin' ? AdminNavbar : role === 'facilitator' ? FacilitatorNavbar : LearnerNavbar;

  useEffect(() => {
    if (currentUser && moduleId) {
      loadModuleData();
    }
  }, [moduleId, currentUser]);

  const loadModuleData = async () => {
    try {
      setLoading(true);

      // Fetch module
      const moduleDoc = await getDoc(doc(db, 'modules', moduleId));
      if (!moduleDoc.exists()) {
        navigate(backRoute);
        return;
      }

      const moduleData = { id: moduleDoc.id, ...moduleDoc.data() };
      setModule(moduleData);

      // Fetch user progress
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const progress = {
          moduleProgress: userData.contentProgress || {}
        };
        setUserProgress(progress);

        const moduleProgress = progress.moduleProgress[moduleId];
        if (moduleProgress && moduleData.lessons?.length > 0) {
          // Set current lesson or first incomplete lesson
          const currentLessonId = moduleProgress.currentLesson ||
            moduleData.lessons.find(lesson => !(moduleProgress.completedLessons || []).includes(lesson.id))?.id ||
            moduleData.lessons[0].id;
          setSelectedLessonId(currentLessonId);
          setCurrentLesson(moduleData.lessons.find(l => l.id === currentLessonId));
        } else if (moduleData.lessons?.length > 0) {
          // Start with first lesson if no progress
          setSelectedLessonId(moduleData.lessons[0].id);
          setCurrentLesson(moduleData.lessons[0]);
        }
      }
    } catch (error) {
      devError('Error loading module:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/login');
    } catch (error) {
      devError('Failed to log out', error);
    }
  };

  const selectLesson = (lesson) => {
    setSelectedLessonId(lesson.id);
    setCurrentLesson(lesson);
  };

  const markLessonComplete = (lessonId) => {
    // Mock function - in real app this would update the database
    devLog(`Marking lesson ${lessonId} as complete`);
    // For now, just move to next lesson
    const currentIndex = module.lessons.findIndex(l => l.id === lessonId);
    if (currentIndex < module.lessons.length - 1) {
      const nextLesson = module.lessons[currentIndex + 1];
      selectLesson(nextLesson);
    }
  };

  const getModuleProgress = () => {
    if (!userProgress?.moduleProgress[moduleId]) return 0;
    return userProgress.moduleProgress[moduleId].progress;
  };

  const getCompletedLessons = () => {
    if (!userProgress?.moduleProgress[moduleId]) return [];
    return userProgress.moduleProgress[moduleId].completedLessons || [];
  };

  const isLessonCompleted = (lessonId) => {
    return getCompletedLessons().includes(lessonId);
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner': return 'success';
      case 'Intermediate': return 'warning';
      case 'Advanced': return 'danger';
      default: return 'secondary';
    }
  };

  const getLessonIcon = (type) => {
    switch (type) {
      case 'video': return '📹';
      case 'project': return '🚀';
      case 'quiz': return '❓';
      case 'reading': return '📚';
      default: return '📄';
    }
  };

  if (loading || !module) {
    return (
      <>
        <Navbar />
        <Container className="mt-5">
          <div className="text-center py-5">
            <Spinner animation="border" role="status" variant="primary">
              <span className="visually-hidden">Loading module...</span>
            </Spinner>
            <p className="mt-3 text-muted">Loading module content...</p>
          </div>
        </Container>
      </>
    );
  }

  return (
    <>
      <Navbar />

      <Container className="mt-4">
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <h1>{module.moduleName}</h1>
                <p className="text-muted">{module.description}</p>
                <div className="d-flex gap-2 mb-3">
                  <Badge bg={getDifficultyColor(module.difficulty)}>
                    {module.difficulty}
                  </Badge>
                  <Badge bg="secondary">{module.estimatedHours}h</Badge>
                  <Badge bg="info">{module.lessons.length} lessons</Badge>
                </div>
              </div>
              <Button variant="outline-secondary" onClick={() => navigate(backRoute)}>
                Back to Dashboard
              </Button>
            </div>
            
            <div className="mb-3">
              <div className="d-flex justify-content-between mb-1">
                <small>Module Progress</small>
                <small>{getModuleProgress()}%</small>
              </div>
              <ProgressBar now={getModuleProgress()} variant="success" />
            </div>
          </Col>
        </Row>

        <Row>
          {/* Lesson List Sidebar */}
          <Col md={4} className="mb-4">
            <Card>
              <Card.Header>
                <h5>Lessons</h5>
              </Card.Header>
              <ListGroup variant="flush">
                {module.lessons.map((lesson, index) => (
                  <ListGroup.Item
                    key={lesson.id}
                    action
                    active={selectedLessonId === lesson.id}
                    onClick={() => selectLesson(lesson)}
                    className="d-flex justify-content-between align-items-start"
                  >
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <span>{getLessonIcon(lesson.type)}</span>
                        <strong>{lesson.title}</strong>
                        {isLessonCompleted(lesson.id) && (
                          <Badge bg="success" className="ms-auto">✓</Badge>
                        )}
                      </div>
                      <small className="text-muted">{lesson.duration}</small>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </Card>
          </Col>

          {/* Lesson Content */}
          <Col md={8}>
            {currentLesson && (
              <Card>
                <Card.Header>
                  <div className="d-flex justify-content-between align-items-center">
                    <h4>{currentLesson.title}</h4>
                    <Badge bg="primary">{currentLesson.type}</Badge>
                  </div>
                </Card.Header>
                <Card.Body>
                  <div className="mb-3">
                    <small className="text-muted">Duration: {currentLesson.duration}</small>
                  </div>
                  
                  {/* Mock content based on lesson type */}
                  {currentLesson.type === 'video' && (
                    <div className="mb-4">
                      <div 
                        className="bg-dark text-white d-flex align-items-center justify-content-center"
                        style={{ height: '300px', borderRadius: '8px' }}
                      >
                        <div className="text-center">
                          <h2>📹</h2>
                          <p>Video: {currentLesson.title}</p>
                          <Button variant="light" size="sm">Play Video</Button>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {currentLesson.type === 'project' && (
                    <div className="mb-4">
                      <div 
                        className="bg-info text-white d-flex align-items-center justify-content-center"
                        style={{ height: '200px', borderRadius: '8px' }}
                      >
                        <div className="text-center">
                          <h2>🚀</h2>
                          <p>Project: {currentLesson.title}</p>
                          <Button variant="light" size="sm">View Project Brief</Button>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="mb-4">
                    <h5>Lesson Content</h5>
                    <p>{currentLesson.content}</p>
                  </div>
                  
                  <div className="mb-4">
                    <h5>Learning Objectives</h5>
                    <ul>
                      {module.learningObjectives.slice(0, 2).map((objective, index) => (
                        <li key={index}>{objective}</li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      {isLessonCompleted(currentLesson.id) ? (
                        <Badge bg="success" className="p-2">
                          ✓ Completed
                        </Badge>
                      ) : (
                        <Button 
                          variant="success"
                          onClick={() => markLessonComplete(currentLesson.id)}
                        >
                          Mark as Complete
                        </Button>
                      )}
                    </div>
                    
                    <div>
                      {(() => {
                        const currentIndex = module.lessons.findIndex(l => l.id === selectedLessonId);
                        return (
                          <div className="d-flex gap-2">
                            {currentIndex > 0 && (
                              <Button 
                                variant="outline-secondary"
                                onClick={() => selectLesson(module.lessons[currentIndex - 1])}
                              >
                                Previous
                              </Button>
                            )}
                            {currentIndex < module.lessons.length - 1 && (
                              <Button 
                                variant="primary"
                                onClick={() => selectLesson(module.lessons[currentIndex + 1])}
                              >
                                Next
                              </Button>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default ModulePage;